import { useDebounceFn } from "@vueuse/core";
import localForage from "localforage";
import { effectScope, watch } from "vue";
import { monitoringCaptureError } from "@/plugins/monitoring";

/*
 *	Cache for store list snapshots. Own localforage instance, so keys never collide
 *	with the outputs cache (which purges by key pattern). Lists are stored as JSON
 *	strings: the arrays are Vue reactive proxies, which structured clone rejects.
 */
const listCacheStore = localForage.createInstance({ name: "signaloidLists", storeName: "lists" });

// Keys are per user: a second account on this browser must not read the first one's lists.
let listCacheScope: string | undefined;

// Logout always ends in a full page load, so a user fetch that lands after the purge must not cache again.
let hasPurgedForLogout = false;

export function setListCacheScope(userID: string): void {
	if (hasPurgedForLogout || userID === listCacheScope) {
		return;
	}
	listCacheScope = userID;
	clearOtherUsersLists(userID).catch((error) => reportFirstCacheFailure(error, "Clear cached lists"));
}

async function clearOtherUsersLists(userID: string): Promise<void> {
	const storageKeys = await listCacheStore.keys();
	const otherUsersKeys = storageKeys.filter((storageKey) => !storageKey.endsWith(`:${userID}`));
	await Promise.all(otherUsersKeys.map((storageKey) => listCacheStore.removeItem(storageKey)));
}

function scopedKey(key: string): string | undefined {
	return listCacheScope === undefined ? undefined : `${key}:${listCacheScope}`;
}

// Writes run on every store mutation, so report each kind of failure once per session.
const reportedFailureContexts = new Set<string>();

function reportFirstCacheFailure(error: unknown, context: string): void {
	if (reportedFailureContexts.has(context)) {
		return;
	}
	reportedFailureContexts.add(context);
	monitoringCaptureError(error, context);
}

// What each key already holds, so an unchanged list is never rewritten.
const lastWrittenPayloads = new Map<string, string>();

function removeCachedList(storageKey: string, kind: "list" | "record"): void {
	lastWrittenPayloads.delete(storageKey);
	listCacheStore.removeItem(storageKey).catch((error) => reportFirstCacheFailure(error, `Remove cached ${kind}`));
}

async function readCachedValue<T>(
	key: string,
	kind: "list" | "record",
	validate: (parsedValue: unknown) => T,
): Promise<T | undefined> {
	const storageKey = scopedKey(key);
	if (storageKey === undefined) {
		return undefined;
	}
	let serializedValue: string | null;
	try {
		serializedValue = await listCacheStore.getItem<string>(storageKey);
	} catch (error) {
		reportFirstCacheFailure(error, `Read cached ${kind}`);
		return undefined;
	}
	if (typeof serializedValue !== "string") {
		return undefined;
	}
	// A write that landed during this read is newer than what the read returned.
	const isPayloadTracked = lastWrittenPayloads.has(storageKey);
	try {
		const validValue = validate(JSON.parse(serializedValue));
		if (!isPayloadTracked) {
			lastWrittenPayloads.set(storageKey, serializedValue);
		}
		return validValue;
	} catch (error) {
		reportFirstCacheFailure(error, `Corrupt cached ${kind}`);
		if (!isPayloadTracked) {
			removeCachedList(storageKey, kind);
		}
		return undefined;
	}
}

/**
 * Reads a cached list, dropping entries that fail `isValidEntry`. Anything
 * unreadable resolves to `undefined`, and the caller just waits for the network.
 */
export async function readCachedList<T>(
	key: string,
	isValidEntry: (entry: unknown) => entry is T,
): Promise<T[] | undefined> {
	return readCachedValue(key, "list", (parsedList) => {
		if (!Array.isArray(parsedList)) {
			throw new Error("Cached list is not an array");
		}
		const validEntries = parsedList.filter(isValidEntry);
		// An empty cached list is valid. Only a nonempty payload where nothing validates is corrupt.
		if (validEntries.length === 0 && parsedList.length > 0) {
			throw new Error("Cached list has no valid entry");
		}
		return validEntries;
	});
}

// Paginated histories grow without bound, so their callers pass this to cache only the first screen.
export const LIST_CACHE_MAX_ENTRIES = 50;

function writeCachedValue(key: string, kind: "list" | "record", value: unknown): void {
	const storageKey = scopedKey(key);
	if (storageKey === undefined) {
		return;
	}
	if (value === undefined) {
		removeCachedList(storageKey, kind);
		return;
	}
	const payload = JSON.stringify(value);
	if (lastWrittenPayloads.get(storageKey) === payload) {
		return;
	}
	lastWrittenPayloads.set(storageKey, payload);
	listCacheStore.setItem(storageKey, payload).catch((error) => {
		// Let the next attempt through rather than treating a failed write as stored.
		lastWrittenPayloads.delete(storageKey);
		reportFirstCacheFailure(error, `Write cached ${kind}`);
	});
}

/**
 * Fire-and-forget: a failed cache write must never break the action that triggered it.
 */
export function writeCachedList(key: string, list: unknown[] | undefined, maxEntries?: number): void {
	writeCachedValue(key, "list", maxEntries === undefined ? list : list?.slice(0, maxEntries));
}

/** Anything unreadable or failing `isValidRecord` resolves to `undefined`. */
export async function readCachedRecord<T extends object>(
	key: string,
	isValidRecord: (record: unknown) => record is T,
): Promise<T | undefined> {
	return readCachedValue(key, "record", (parsedRecord) => {
		if (!isValidRecord(parsedRecord)) {
			throw new Error("Cached record is not valid");
		}
		return parsedRecord;
	});
}

export function writeCachedRecord(key: string, record: object): void {
	writeCachedValue(key, "record", record);
}

export function removeCachedRecord(key: string): void {
	writeCachedValue(key, "record", undefined);
}

export const LIST_CACHE_WRITE_DEBOUNCE_MS = 300;

const mirrorWrites: (() => void)[] = [];
window.addEventListener("pagehide", () => mirrorWrites.forEach((writeMirror) => writeMirror()));

/** Writes the list to the cache whenever the getter returns a different array, debounced against status storms. */
export function mirrorListToCache(key: string, getList: () => unknown[] | undefined, maxEntries?: number): void {
	const writeMirror = () => writeCachedList(key, getList(), maxEntries);
	mirrorWrites.push(writeMirror);
	effectScope(true).run(() => {
		const persist = useDebounceFn(writeMirror, LIST_CACHE_WRITE_DEBOUNCE_MS);
		watch(getList, () => persist());
	});
}

const mirroredStoreKeys = new WeakMap<object, Set<string>>();

/** Mirrors a store's list to the cache, starting once per store and key however often the store asks. */
export function mirrorStoreListToCache(
	store: object,
	key: string,
	getList: () => unknown[] | undefined,
	maxEntries?: number,
): void {
	const mirroredKeys = mirroredStoreKeys.get(store) ?? new Set<string>();
	if (mirroredKeys.has(key)) {
		return;
	}
	mirroredKeys.add(key);
	mirroredStoreKeys.set(store, mirroredKeys);
	mirrorListToCache(key, getList, maxEntries);
}

/**
 * Reads the cached list for a store that may already hold some entries. Resolves the list to show,
 * or undefined to leave the store as it is.
 */
export async function hydrateCachedList<T>(
	key: string,
	isValidEntry: (entry: unknown) => entry is T,
	getList: () => T[] | undefined,
	idOf: (entry: T) => string,
): Promise<T[] | undefined> {
	if (getList() !== undefined) {
		return undefined;
	}
	const cachedList = await readCachedList(key, isValidEntry);
	if (cachedList === undefined) {
		return undefined;
	}
	const receivedDuringRead = getList();
	if (receivedDuringRead === undefined) {
		return cachedList;
	}
	const receivedIDs = new Set(receivedDuringRead.map(idOf));
	return [...cachedList.filter((entry) => !receivedIDs.has(idOf(entry))), ...receivedDuringRead];
}

/**
 * Purges every cached list. Runs on logout so the next account never reads this
 * one's data, and resolves once the purge has committed so callers can await it
 * before navigating away.
 */
export async function clearCachedLists(): Promise<void> {
	// A write already in flight still lands before clear(), since IndexedDB runs transactions in order.
	hasPurgedForLogout = true;
	listCacheScope = undefined;
	lastWrittenPayloads.clear();
	try {
		await listCacheStore.clear();
	} catch (error) {
		reportFirstCacheFailure(error, "Purge cached lists on logout");
	}
}
